import {
	Chip,
	CircularProgress,
	Grid,
	InputAdornment,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Paper,
	TextField,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { get } from "src/utils/networkUtils";
import { getUserPermissions } from "src/utils/permissionsUtils";
import Iconify from "../iconify/Iconify";

export default function InboxUserSearch(props) {
	const { users, onUserSelect } = props;

	const [searchQuery, setSearchQuery] = useState("");
	const [searchResults, setSearchResults] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [showResults, setShowResults] = useState(false);
	const searchRef = useRef(null);
	const { isViewer } = getUserPermissions();

	useEffect(() => {
		setSelectedUsers(users || []);
	}, [users]);

	useEffect(() => {
		if (!searchQuery || searchQuery.length < 3) {
			setSearchResults([]);
			return;
		}
		setLoading(true);
		const getUsers = setTimeout(() => {
			get(`/admin/users/search?query=${searchQuery}`)
				.then((response) => {
					setSearchResults(response?.users || []);
				})
				.finally(() => setLoading(false));
		}, 500);

		return () => clearTimeout(getUsers);
	}, [searchQuery]);

	useEffect(() => {
		const userids = selectedUsers.map((user) => user.id);
		onUserSelect(userids);
	}, [selectedUsers, onUserSelect]);

	useEffect(() => {
		if (!showResults) return;
		const handleClickOutside = (event) => {
			if (searchRef.current && !searchRef.current.contains(event.target)) {
				setShowResults(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [showResults]);

	const handleSelect = (user) => {
		setSelectedUsers((prevSelected) => {
			const isSelected = prevSelected.some((selectedUser) => selectedUser.id === user.id);
			if (isSelected) {
				return prevSelected.filter((selectedUser) => selectedUser.id !== user.id);
			} else {
				return [...prevSelected, user];
			}
		});
	};
	const handleDelete = (user) => {
		setSelectedUsers((prevSelected) => prevSelected.filter((selectedUser) => selectedUser.id !== user.id));
	};

	return (
		<Grid container spacing={2}>
			<Grid item xs={3} sx={{ position: "relative" }} ref={searchRef}>
				<TextField
					margin="dense"
					id="kickoff"
					label="Search"
					type="text"
					fullWidth
					variant="standard"
					value={searchQuery}
					onFocus={() => setShowResults(true)}
					onChange={(e) => {
						setSearchQuery(e.target.value);
						setShowResults(true);
					}}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Iconify icon={"eva:search-outline"} />
							</InputAdornment>
						),
						endAdornment: searchQuery && (
							<InputAdornment position="end">
								<Iconify
									icon={"eva:close-outline"}
									onClick={() => {
										setSearchQuery("");
										setSearchResults([]);
										setShowResults(false);
									}}
									sx={{ cursor: "pointer" }}
								/>
							</InputAdornment>
						),
					}}
					autoComplete="off"
				/>
				{searchQuery && showResults && (
					<Paper
						elevation={2}
						sx={{
							position: "absolute",
							zIndex: 10,
							width: "100%",
							maxHeight: 300,
							overflowY: "auto",
						}}
					>
						{loading ? (
							<CircularProgress size={24} sx={{ m: 2 }} />
						) : (
							<List dense>
								{searchResults.length === 0 ? (
									<ListItem>
										<ListItemText primary="No results found" />
									</ListItem>
								) : (
									searchResults.map((user) => {
										const isSelected = selectedUsers.some((selectedUser) => selectedUser.id === user.id);
										return (
											<ListItemButton
												key={user.id || user.userId}
												onClick={() => handleSelect(user)}
												selected={isSelected}
												sx={{
													"&:hover": {
														backgroundColor: "rgba(0, 0, 0, 0.08)",
													},
												}}
											>
												<ListItemText
													primary={user.username || user.email || user.name}
													secondary={user.email && user.username !== user.email ? user.email : null}
												/>
											</ListItemButton>
										);
									})
								)}
							</List>
						)}
					</Paper>
				)}
			</Grid>
			{!isViewer && (
				<Grid item xs={9}>
					{selectedUsers.length > 0 &&
						selectedUsers.map((user) => (
							<Chip label={user.username} variant="filled" onDelete={() => handleDelete(user)} key={user.id} sx={{ margin: 0.5 }} />
						))}
				</Grid>
			)}
		</Grid>
	);
}
